// Typed Tauri event listeners.

import { listen as tauriListen, type UnlistenFn } from "@tauri-apps/api/event";
import type {
  FinalGuessEvent,
  PipelineResultEvent,
  PipelineStartedEvent,
} from "../types/contract";

const isTauri = typeof window !== "undefined" && window.hasOwnProperty("__TAURI_IPC__");

const listen = <T>(event: string, handler: (e: any) => void): Promise<UnlistenFn> => {
  if (!isTauri) {
    // Return a dummy unlisten function when running on the web
    return Promise.resolve(() => {});
  }
  return tauriListen<T>(event, handler);
};

export const onPipelineStarted = (
  fn: (e: PipelineStartedEvent) => void,
): Promise<UnlistenFn> =>
  listen<PipelineStartedEvent>("pipeline://started", (e) => fn(e.payload));

export const onPipelineResult = (
  fn: (e: PipelineResultEvent) => void,
): Promise<UnlistenFn> =>
  listen<PipelineResultEvent>("pipeline://result", (e) => fn(e.payload));

/// Synthesized ensemble pinpoint emitted after all LLM model slots
/// resolve. UI consumers route this into the FinalGuessFooter's
/// `setFinal` setter — distinct from per-model `pipeline://result`
/// events which carry one model's guess each.
export const onFinalGuess = (
  fn: (e: FinalGuessEvent) => void,
): Promise<UnlistenFn> =>
  listen<FinalGuessEvent>("state://final_guess", (e) => fn(e.payload));

/// Game-monitor scene-change. Emitted on the perceptual-hash
/// poll path when the foreground GeoGuessr window changes scene
/// (typically: round transition or score-screen reveal). Off
/// unless [game].auto_detect_enabled = true in user.toml.
export interface SceneChangeEvent {
  hamming: number;
  threshold: number;
  at_ms: number;
  window_title: string | null;
}

export const onSceneChange = (
  fn: (e: SceneChangeEvent) => void,
): Promise<UnlistenFn> =>
  listen<SceneChangeEvent>("monitor://scene_change", (e) => fn(e.payload));

/// Auto-truth toast emitted by the game-monitor's score-screen
/// detection path (Phase 6) when it successfully extracts the
/// round's actual answer and records it to the stats DB. The
/// payload is a pre-formatted human-readable string so the
/// toast handler doesn't have to know the structured shape.
export const onAutoTruthToast = (
  fn: (msg: string) => void,
): Promise<UnlistenFn> =>
  listen<string>("toast://auto-truth", (e) => fn(e.payload));

export const onSetOpacity = (fn: (o: number) => void): Promise<UnlistenFn> =>
  listen<number>("overlay://set_opacity", (e) => fn(e.payload));

export const onRoundReset = (fn: () => void): Promise<UnlistenFn> =>
  // The Rust emitter sends a unit `()` payload; typing as `null`
  // (the JSON encoding of unit) instead of `unknown` makes a future
  // payload-bearing emitter visible at the type-check boundary.
  listen<null>("state://round_reset", () => fn());

export const onHotkey = (fn: (kind: string) => void): Promise<UnlistenFn> =>
  listen<string>("hotkey", (e) => fn(e.payload));

export interface StreamSafetyEvent {
  stream_safe: boolean;
  build: number | null;
  min_build: number;
  /// Two orthogonal signals: `set_call_succeeded` is one-shot (the
  /// SetWindowDisplayAffinity startup result), `in_remote_session` is
  /// the live RDP signal polled every 30 s.
  set_call_succeeded: boolean;
  in_remote_session: boolean;
}

/// Emitted once at startup so the UI can render a persistent banner
/// when WDA_EXCLUDEFROMCAPTURE isn't honoured on this Windows build —
/// streaming Atlas in that state would let the overlay leak into OBS.
export const onStreamSafety = (
  fn: (e: StreamSafetyEvent) => void,
): Promise<UnlistenFn> =>
  listen<StreamSafetyEvent>("capability://stream_safe", (e) => fn(e.payload));

export interface ReverseImageStalenessEvent {
  yandex_ok: boolean;
  lens_ok: boolean;
  bing_ok: boolean;
}

/// Fired once at startup with the result of HEADing each
/// reverse-image search engine's URL template. UI shows a banner
/// listing any engine that failed so the user has time to fall back
/// before a tournament round.
export const onReverseImageStaleness = (
  fn: (e: ReverseImageStalenessEvent) => void,
): Promise<UnlistenFn> =>
  listen<ReverseImageStalenessEvent>(
    "capability://reverse_image_staleness",
    (e) => fn(e.payload),
  );

export interface HotkeyRegisterFailureItem {
  binding: string;
  spec: string;
  error: string;
}

/// Wrapper payload that carries success counters alongside the
/// failures array so the banner can render a concrete
/// "17 / 18 hotkeys registered" diagnostic. The listener type is
/// just `HotkeyRegistrationFailuresPayload` — no union with a
/// bare-array shape, since UI and Rust ship together as a single
/// MSI bundle.
export interface HotkeyRegistrationFailuresPayload {
  registered: number;
  total: number;
  failures: HotkeyRegisterFailureItem[];
}

/// Emitted at startup if any hotkey binding failed to register
/// (typo in `user.toml`, OS-level shortcut conflict, etc). The
/// payload lists the affected bindings so the UI can warn the user
/// without blocking startup — the rest of the bindings still work.
export const onHotkeyRegisterFailures = (
  fn: (payload: HotkeyRegistrationFailuresPayload) => void,
): Promise<UnlistenFn> =>
  listen<HotkeyRegistrationFailuresPayload>(
    "capability://hotkey_registration_failures",
    (e) => fn(e.payload),
  );
